/**
 * Token Manager - Handles access and refresh tokens with proactive refresh logic
 */

const TOKEN_REFRESH_THRESHOLD = 5000; // 5 seconds before expiry

class TokenManager {
  private refreshPromise: Promise<void> | null = null;

  /**
   * Save tokens to localStorage with expiration timestamp
   */
  saveTokens(
    accessToken: string,
    refreshToken: string,
    expiresIn: number
  ): void {
    const expiresAt = Date.now() + expiresIn * 1000; // convert seconds to milliseconds

    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('token_expires_at', expiresAt.toString());
  }

  /**
   * Get access token (will refresh if close to expiry)
   */
  async getAccessToken(): Promise<string | null> {
    const accessToken = localStorage.getItem('access_token');
    const expiresAt = localStorage.getItem('token_expires_at');

    if (!accessToken || !expiresAt) {
      return null;
    }

    const timeUntilExpiry = parseInt(expiresAt) - Date.now();

    // If token expires in less than 5 seconds, refresh it proactively
    if (timeUntilExpiry < TOKEN_REFRESH_THRESHOLD) {
      await this.refreshAccessToken();
      return localStorage.getItem('access_token');
    }

    return accessToken;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Refresh the access token using refresh token
   */
  async refreshAccessToken(): Promise<void> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefresh();

    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async _doRefresh(): Promise<void> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.clearTokens();
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://api.thilo.loc:8000/v2/api'}/tenants/auth/refresh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        }
      );

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Save new tokens
      this.saveTokens(
        data.tokens.access_token,
        data.tokens.refresh_token,
        data.tokens.expires_in
      );
    } catch (error) {
      // Refresh token is invalid or expired
      this.clearTokens();
      window.location.href = '/login';
      throw error;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  }

  /**
   * Clear all tokens from storage
   */
  clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
  }

  /**
   * Check if token needs refresh soon
   */
  shouldRefreshToken(): boolean {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return false;

    const timeUntilExpiry = parseInt(expiresAt) - Date.now();
    return timeUntilExpiry < TOKEN_REFRESH_THRESHOLD;
  }
}

export const tokenManager = new TokenManager();
